import { useState } from "react";
import { Search } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { TChatRoom } from "@/redux/feature/chat/chatType";
import { timeAgo } from "@/utils/relativeTime";
import { useGetUserOnlineOfflineStatusQuery } from "@/redux/feature/chat/chatApi";

// export interface Conversation {
//   id: string;
//   name: string;
//   avatar?: string;
//   lastMessage: string;
//   timestamp: string;
//   unreadCount?: number;
//   isOnline?: boolean;
// }

interface ConversationListProps {
  conversations: TChatRoom[];
  selectedId?: string;
  onSelect: (conversation: TChatRoom) => void;
  className?: string;
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  className,
}: ConversationListProps) {
  const [activeTab, setActiveTab] = useState<"new" | "all">("new");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredConversations = conversations.filter(
    (conv) =>
      (conv.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conv.lastMessage || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()),
  );

  const newCount = conversations.filter(
    (c) => c.unreadCount && c.unreadCount > 0,
  ).length;

  return (
    <div
      className={cn(
        "flex flex-col h-full bg-card border-r border-border",
        className,
      )}
    >
      {/* Search */}
      <div className="p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search messages"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-muted rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <TabButton
            active={activeTab === "new"}
            onClick={() => setActiveTab("new")}
            count={newCount}
          >
            New
          </TabButton>
          <TabButton
            active={activeTab === "all"}
            onClick={() => setActiveTab("all")}
            count={conversations.length}
          >
            All
          </TabButton>
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-2 pb-4">
        <div className="space-y-1">
          {filteredConversations.map((conversation, index) => (
            <ConversationCard
              key={conversation._id}
              conversation={conversation}
              isSelected={selectedId === conversation._id}
              onClick={() => onSelect(conversation)}
              style={{ animationDelay: `${index * 50}ms` }}
            />
          ))}
        </div>

        {filteredConversations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <p className="text-sm">No conversations found</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
        active
          ? "bg-foreground text-background"
          : "bg-muted text-muted-foreground hover:bg-muted/80",
      )}
    >
      {children}
      <span
        className={cn(
          "text-xs",
          active ? "text-background/70" : "text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}

function ConversationCard({
  conversation,
  isSelected,
  onClick,
  style,
}: {
  conversation: TChatRoom;
  isSelected: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
}) {
  const { data: OnlineStatusDTO } = useGetUserOnlineOfflineStatusQuery(
    conversation?.client_id?._id,
    { skip: !conversation?.client_id?._id },
  );

  const onLineStatus = OnlineStatusDTO?.data?.online;
  return (
    <div
      onClick={onClick}
      className={cn(
        "conversation-card animate-fade-in-up",
        isSelected && "conversation-card-active",
      )}
      style={style}
    >
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0">
          <Avatar className="w-10 h-10">
            <AvatarImage src={conversation.avatar} />
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
              {conversation?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          {onLineStatus && (
            <span className="absolute bottom-0 right-0 online-indicator ring-2 ring-card" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground truncate">
              {conversation.name}
            </p>
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {timeAgo(conversation.timestamp as string)}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-sm text-muted-foreground truncate flex-1">
              {conversation.lastMessage}
            </p>
            {conversation.unreadCount && conversation.unreadCount > 0 && (
              <span className="unread-badge flex-shrink-0">
                +{conversation.unreadCount} messages
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
